import * as B from '@blueprintjs/core';
import {DateRangeInput3} from '@blueprintjs/datetime2';
import moment from 'moment';
import React from 'react';
import {TableInstance} from 'react-table';

import ActiveFiltersCountButton from 'app/components/ActiveFiltersCountButton/ActiveFiltersCountButton';
import {ApiProjectReport} from 'app/modules/Remote/Feature';

import {SearchBoxFilter} from '../ColumnFilters';
import {TableSortMenu} from '../PropertyTable';
import {countActiveFilters} from '../tableUrlState';
import {MultiSelectFilterButton} from './PortfolioReportsColumnFilters';
import {
  AUTHOR_COLUMN_ID,
  CREATED_COLUMN_ID,
  PROPERTY_COLUMN_ID,
  SORTABLE_COLUMN_IDS,
  TITLE_COLUMN_ID,
  TYPE_COLUMN_ID,
  decodeDateRange,
  encodeDateRange,
  isYearDateRange,
  reportAuthors,
  reportProperties,
  reportTypes,
  reportYears,
  yearDateRange,
} from './reportsTableState';
import cs from './styles.module.scss';

const PortfolioReportsControls: React.FunctionComponent<{
  table: TableInstance<ApiProjectReport>;
  reports: ApiProjectReport[];
}> = ({table, reports}) => {
  const columnById = (id: string) => table.columns.find((col) => col.id === id)!;
  const titleColumn = columnById(TITLE_COLUMN_ID);
  const createdColumn = columnById(CREATED_COLUMN_ID);
  const typeColumn = columnById(TYPE_COLUMN_ID);
  const propertyColumn = columnById(PROPERTY_COLUMN_ID);
  const authorColumn = columnById(AUTHOR_COLUMN_ID);

  const sortableColumns = table.columns.filter((col) => SORTABLE_COLUMN_IDS.includes(col.id));
  const sortColumn = sortableColumns.find((col) => col.isSorted) ?? createdColumn;

  const activeFilterCount = countActiveFilters(table.state.filters);
  const years = React.useMemo(() => reportYears(reports), [reports]);
  const types = React.useMemo(() => reportTypes(reports), [reports]);
  const properties = React.useMemo(() => reportProperties(reports), [reports]);
  const authors = React.useMemo(() => reportAuthors(reports), [reports]);
  const dateRange = decodeDateRange(createdColumn.filterValue);

  const filterPanel = (
    <div className={cs.filterPanel}>
      <B.FormGroup label="Created">
        <B.ButtonGroup className={cs.yearButtons}>
          <B.Button
            small
            text="All"
            active={!dateRange[0] && !dateRange[1]}
            onClick={() => table.setFilter(CREATED_COLUMN_ID, undefined)}
          />
          {years.map((year) => (
            <B.Button
              key={year}
              small
              text={String(year)}
              active={isYearDateRange(dateRange, year)}
              onClick={() =>
                table.setFilter(CREATED_COLUMN_ID, encodeDateRange(yearDateRange(year)))
              }
            />
          ))}
        </B.ButtonGroup>
        <DateRangeInput3
          value={dateRange}
          onChange={(range) => table.setFilter(CREATED_COLUMN_ID, encodeDateRange(range))}
          formatDate={(date) => moment(date).format('L')}
          parseDate={(str) => moment(str, 'L').toDate()}
          allowSingleDayRange
          shortcuts={false}
          popoverProps={{captureDismiss: true}}
          startInputProps={{small: true, placeholder: 'Start date'}}
          endInputProps={{small: true, placeholder: 'End date'}}
        />
      </B.FormGroup>
      <B.FormGroup label="Type">
        <B.HTMLSelect
          fill
          value={typeColumn.filterValue ?? ''}
          onChange={(ev) => table.setFilter(TYPE_COLUMN_ID, ev.currentTarget.value || undefined)}
          options={[
            {label: 'All types', value: ''},
            ...types.map((type) => ({label: type, value: type})),
          ]}
        />
      </B.FormGroup>
      <B.FormGroup label="Property">
        <MultiSelectFilterButton
          allText="All properties"
          pluralNoun="properties"
          options={properties}
          value={propertyColumn.filterValue}
          setValue={(value) => table.setFilter(PROPERTY_COLUMN_ID, value)}
        />
      </B.FormGroup>
      <B.FormGroup label="Author">
        <MultiSelectFilterButton
          allText="All authors"
          pluralNoun="authors"
          options={authors}
          value={authorColumn.filterValue}
          setValue={(value) => table.setFilter(AUTHOR_COLUMN_ID, value)}
        />
      </B.FormGroup>
    </div>
  );

  return (
    <div className={cs.controls}>
      <B.ButtonGroup>
        <B.Popover content={filterPanel} position={B.Position.BOTTOM_LEFT}>
          <ActiveFiltersCountButton count={activeFilterCount} outlined />
        </B.Popover>
        {activeFilterCount > 0 && (
          <B.Tooltip content="Clear all filters">
            <B.Button icon="filter-remove" outlined onClick={() => table.setAllFilters([])} />
          </B.Tooltip>
        )}
      </B.ButtonGroup>
      <TableSortMenu columns={sortableColumns} sortColumn={sortColumn} />
      <SearchBoxFilter
        setFilter={table.setFilter}
        column={titleColumn}
        placeholder="Search documents…"
      />
    </div>
  );
};

export default PortfolioReportsControls;
